import React, { useEffect, useState } from "react";
import styles from "./Screensaver.module.css";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { useApp } from "../../contexts/AppContext";
import { t } from "../../utils/localization";

export default function Screensaver() {
  const { state, setScreensaverActive } = useApp();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const {
    data: screensavers,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["screensaver", { active: true }],
    queryFn: () => apiService.getScreensavers({ active: true }),
    staleTime: 300000, // 5 minutes
  });

  const handleClick = () => {
    setScreensaverActive(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
      setScreensaverActive(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
        setScreensaverActive(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setScreensaverActive]);

  // Auto-advance slides with video support
  useEffect(() => {
    if (!screensavers || screensavers.length <= 1) return;

    const slideDelay = state.appSettings?.screensaverSlideDelay || 5000; // Default 5 seconds
    console.log("Screensaver slide delay:", slideDelay, "ms");

    let timeoutId: NodeJS.Timeout;

    const scheduleNextSlide = () => {
      const currentScreensaverData = screensavers[currentSlideIndex];

      if (currentScreensaverData?.type === "VIDEO") {
        // For videos, wait for the video to end
        const videoElement = document.querySelector(
          `video[data-screensaver-index="${currentSlideIndex}"]`
        ) as HTMLVideoElement;

        if (videoElement) {
          const handleVideoEnd = () => {
            console.log("Video ended, advancing to next screensaver");
            setCurrentSlideIndex((prev) => (prev + 1) % screensavers.length);
            videoElement.removeEventListener("ended", handleVideoEnd);
          };

          videoElement.addEventListener("ended", handleVideoEnd);

          // Calculate fallback timeout based on video duration or use default
          const fallbackTimeout = videoElement.duration
            ? Math.min(videoElement.duration * 1000 + 2000, 60000) // duration + 2s, max 60s
            : 30000; // 30 seconds fallback if duration unknown

          timeoutId = setTimeout(() => {
            console.log(
              "Screensaver video fallback timeout triggered, advancing slide"
            );
            setCurrentSlideIndex((prev) => (prev + 1) % screensavers.length);
            videoElement.removeEventListener("ended", handleVideoEnd);
          }, fallbackTimeout);
        } else {
          // If video element not found, use default delay
          timeoutId = setTimeout(() => {
            setCurrentSlideIndex((prev) => (prev + 1) % screensavers.length);
          }, slideDelay);
        }
      } else {
        // For images, use configured delay
        timeoutId = setTimeout(() => {
          setCurrentSlideIndex((prev) => (prev + 1) % screensavers.length);
        }, slideDelay);
      }
    };

    // Start the first slide transition
    scheduleNextSlide();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    screensavers,
    currentSlideIndex,
    state.appSettings?.screensaverSlideDelay,
  ]);

  if (isLoading) {
    return (
      <div
        className={styles.screensaverContainer}
        onClick={handleClick}
        onKeyDown={handleKeyPress}
        tabIndex={0}
      >
        <div className={styles.screensaverContent}>
          <div className={styles.loadingSpinner}>
            {t("loading", state.language)}...
          </div>
        </div>
      </div>
    );
  }

  if (error || !screensavers || screensavers.length === 0) {
    return (
      <div
        className={styles.screensaverContainer}
        onClick={handleClick}
        onKeyDown={handleKeyPress}
        tabIndex={0}
      >
        <div className={styles.screensaverContent}>
          <div className={styles.defaultScreensaver}>
            <h1 className={styles.defaultTitle}>
              {t("welcome", state.language)}
            </h1>
            <p className={styles.defaultSubtitle}>
              {state.language === "en"
                ? "Touch anywhere to continue"
                : "Коснитесь экрана для продолжения"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentScreensaver = screensavers[currentSlideIndex];

  return (
    <div
      className={styles.screensaverContainer}
      onClick={handleClick}
      onKeyDown={handleKeyPress}
      tabIndex={0}
    >
      <div className={styles.screensaverContent}>
        {currentScreensaver.type === "IMAGE" ? (
          <img
            className={styles.screensaverImage}
            src={`${currentScreensaver.url}?v=${Date.now()}`}
            alt={currentScreensaver.title || ""}
            onError={(e) => {
              console.log(
                "Screensaver image load error for URL:",
                currentScreensaver.url
              );
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
            }}
            onLoad={() => {
              console.log(
                "Screensaver image loaded successfully:",
                currentScreensaver.url
              );
            }}
          />
        ) : (
          <video
            className={styles.screensaverVideo}
            src={`${currentScreensaver.url}?v=${Date.now()}`}
            autoPlay
            muted
            loop={false}
            preload="metadata"
            data-screensaver-index={currentSlideIndex}
            onError={(e) => {
              console.log(
                "Screensaver video load error for URL:",
                currentScreensaver.url
              );
              const target = e.target as HTMLVideoElement;
              target.style.display = "none";
              // Advance to next slide if video fails to load
              setTimeout(() => {
                setCurrentSlideIndex(
                  (prev) => (prev + 1) % screensavers.length
                );
              }, 1000);
            }}
            onLoadStart={() => {
              console.log(
                "Screensaver video load started:",
                currentScreensaver.url
              );
            }}
            onLoadedMetadata={(e) => {
              console.log(
                "Screensaver video metadata loaded:",
                currentScreensaver.url
              );
              const video = e.target as HTMLVideoElement;
              console.log(
                "Screensaver video duration:",
                video.duration,
                "seconds"
              );
            }}
            onCanPlay={() => {
              console.log(
                "Screensaver video can play:",
                currentScreensaver.url
              );
            }}
            onPlay={() => {
              console.log(
                "Screensaver video started playing:",
                currentScreensaver.url
              );
            }}
            onPause={() => {
              console.log("Screensaver video paused:", currentScreensaver.url);
            }}
          />
        )}

        {currentScreensaver.title && (
          <div className={styles.screensaverTitle}>
            {currentScreensaver.title}
          </div>
        )}

        <div className={styles.touchToContinue}>
          {state.language === "en"
            ? "Touch anywhere to continue"
            : "Коснитесь экрана для продолжения"}
        </div>
      </div>
    </div>
  );
}
